import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, AlertCircle, Lock, MessageCircle, Wifi, WifiOff } from 'lucide-react';
import { tastingApi } from '@/api/services';
import { useTastingStore } from '@/stores/tastingStore';
import { useEventStore } from '@/stores/eventStore';
import { useWebSocket } from '@/hooks/useWebSocket';
import TastingLayout from '@/components/layout/TastingLayout';
import IntroStep from '@/components/steps/IntroStep';
import AromaStep from '@/components/steps/AromaStep';
import TasteStep from '@/components/steps/TasteStep';
import FinishStep from '@/components/steps/FinishStep';
import BotanicalsStep from '@/components/steps/BotanicalsStep';
import SummaryStep from '@/components/steps/SummaryStep';

export default function TastingPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const {
    event,
    guest,
    isStepLocked,
    lockedStep,
    moderatorMessage,
    canProceed,
  } = useEventStore();
  const {
    session,
    setSession,
    setSpirits,
    spirits,
    currentStep,
    currentSpiritIndex,
    getProgress,
  } = useTastingStore();

  // WebSocket connection for real-time updates
  const { isConnected, sendProgress } = useWebSocket({
    accessCode: event?.access_code || '',
    guestId: guest?.id || 0,
    guestName: guest?.name || 'Gast',
    clientType: 'guest',
  });

  // Report progress when step or spirit changes
  useEffect(() => {
    if (event?.tasting_mode !== 'async' && sendProgress) {
      sendProgress(currentStep, currentSpiritIndex);
    }
  }, [currentStep, currentSpiritIndex, event?.tasting_mode, sendProgress]);

  // Fetch session data - always fetch if we don't have spirits
  const needsData = !session || spirits.length === 0;
  const { data: fetchedSession, isLoading, error } = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => tastingApi.getSession(sessionId!),
    enabled: !!sessionId && needsData,
    retry: false,
  });

  // Set session and spirits from query
  useEffect(() => {
    if (fetchedSession) {
      if (!session) {
        setSession(fetchedSession);
      }
      if (fetchedSession.spirits && fetchedSession.spirits.length > 0 && spirits.length === 0) {
        setSpirits(fetchedSession.spirits);
      }
    }
  }, [fetchedSession, session, spirits.length, setSession, setSpirits]);

  // Redirect if session is completed
  useEffect(() => {
    if (session?.status === 'completed') {
      navigate(`/completed/${session.uuid}`);
    }
  }, [session, navigate]);

  if (isLoading) {
    return (
      <TastingLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      </TastingLayout>
    );
  }

  if (error || (!session && !fetchedSession)) {
    return (
      <TastingLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <AlertCircle className="w-16 h-16 text-danger-500 mb-4" />
          <h1 className="text-2xl font-display font-bold text-dark-100 mb-2">
            Session nicht gefunden
          </h1>
          <p className="text-dark-400 max-w-md">
            Die Tasting-Session wurde nicht gefunden oder ist abgelaufen.
          </p>
        </div>
      </TastingLayout>
    );
  }

  const activeSession = session || fetchedSession;

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 'intro':
        return <IntroStep />;
      case 'aroma':
        return <AromaStep />;
      case 'taste':
        return <TasteStep />;
      case 'finish':
        return <FinishStep />;
      case 'botanicals':
        return <BotanicalsStep />;
      case 'summary':
        return <SummaryStep />;
      default:
        return <IntroStep />;
    }
  };

  // Check if current step can proceed
  const isCurrentStepLocked = isStepLocked && !canProceed(currentStep);

  return (
    <TastingLayout
      showBranding={!!event?.branding}
      branding={event?.branding}
      showProgress
      progress={getProgress()}
    >
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Connection Status */}
        {event?.tasting_mode !== 'async' && (
          <div className="fixed top-4 right-4 flex items-center gap-2 text-xs z-50">
            {isConnected ? (
              <div className="flex items-center gap-1 px-2 py-1 bg-success-500/20 text-success-400 rounded-full">
                <Wifi className="w-3 h-3" />
                <span>Live</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 px-2 py-1 bg-warning-500/20 text-warning-400 rounded-full">
                <WifiOff className="w-3 h-3" />
                <span>Offline</span>
              </div>
            )}
          </div>
        )}

        {/* Moderator Message Banner */}
        <AnimatePresence>
          {moderatorMessage && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 p-4 bg-primary-500/10 border border-primary-500/30 rounded-xl"
            >
              <div className="flex items-start gap-3">
                <MessageCircle className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-primary-300 text-sm font-medium mb-1">
                    Moderator:
                  </p>
                  <p className="text-dark-200">{moderatorMessage}</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step Locked Overlay */}
        <AnimatePresence>
          {isCurrentStepLocked && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mb-6 p-4 bg-warning-500/10 border border-warning-500/30 rounded-xl"
            >
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-warning-400" />
                <div>
                  <p className="text-warning-300 font-medium">
                    Bitte warten
                  </p>
                  <p className="text-dark-400 text-sm">
                    Der Moderator hat diesen Schritt noch nicht freigegeben.
                    {lockedStep && ` Aktueller Schritt: ${getStepLabel(lockedStep)}`}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className={isCurrentStepLocked ? 'opacity-50 pointer-events-none' : ''}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    </TastingLayout>
  );
}

// Helper function to get step label
function getStepLabel(step: string): string {
  const labels: Record<string, string> = {
    intro: 'Einführung',
    aroma: 'Aroma',
    taste: 'Geschmack',
    finish: 'Abgang',
    botanicals: 'Botanicals',
    summary: 'Zusammenfassung',
  };
  return labels[step] || step;
}
